import React, { useState, useRef, useEffect } from 'react';
import {
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { IOnboardingTemplateType } from 'core/interfaces/onboardingInterface';
import { uploadOnboardingDocumentFile } from 'core/API/onboarding';
import { validateDocumentFile } from 'module/onboarding/utils/onboardingValidation';
import styles from '../SchedulerSetting.module.scss';

interface UploadDocumentProps {
  existingDocumentsCount: number;
  existingNames: string[];
  templateTypes: IOnboardingTemplateType[];
  handleBackClick?: () => void;
  onDocumentUploaded?: () => void;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  existingDocumentsCount,
  existingNames,
  templateTypes,
  handleBackClick,
  onDocumentUploaded,
}) => {
  const [name, setName] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isManagerSignatureRequired, setIsManagerSignatureRequired] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validatePdfFile = (file: File): string | null => {
    const validationErrors = validateDocumentFile({
      type: file.type,
      size: file.size,
      name: file.name,
    });
    return validationErrors[0] ?? null;
  };

  const handleFileAccepted = (file: File) => {
    const validationError = validatePdfFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    if (existingNames.includes(file.name)) {
      setError(`A document named "${file.name}" already exists. Names must be unique.`);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setError(null);

    if (!name) {
      setName(file.name.replace(/\.pdf$/i, ''));
    }
  };

  const activeTemplateTypes = templateTypes.filter((t) => t.active !== false);

  useEffect(() => {
    // Set default type when templateTypes are available
    if (activeTemplateTypes.length > 0 && !selectedTypeId) {
      setSelectedTypeId(activeTemplateTypes[0].id);
    }
  }, [activeTemplateTypes, selectedTypeId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileAccepted(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFileAccepted(file);
  };

  const handleDocumentTypeChange = (event: SelectChangeEvent<number>) => {
    setSelectedTypeId(Number(event.target.value));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!selectedFile) {
      setError('Please select a PDF file to upload');
      return;
    }

    const fileValidationError = validatePdfFile(selectedFile);
    if (fileValidationError) {
      setError(fileValidationError);
      return;
    }

    if (existingDocumentsCount >= 50) {
      setError('Maximum of 50 documents reached');
      return;
    }

    if (!selectedTypeId) {
      setError('Document type is required');
      return;
    }

    setIsUploading(true);

    try {
      // Next sort order is the count + 1
      const nextSortOrder = existingDocumentsCount + 1;

      await uploadOnboardingDocumentFile(
        selectedFile,
        name.trim(),
        selectedTypeId,
        nextSortOrder,
        isManagerSignatureRequired
      );

      onDocumentUploaded?.();
      setTimeout(() => {
        handleBackClick?.();
      }, 500);
    } catch (err: any) {
      setError(err?.message || 'Failed to upload document');
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.editGeneralSettings}>
      <Grid container direction="column" spacing={2} pl={2}>
        <Grid container direction="row" justifyContent="space-between">
          <Button className={styles.backButton} onClick={handleBackClick} variant="contained">
            Back
          </Button>
          <Button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile}
            variant="contained"
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Grid>
        <Grid>
          <h1 className={styles.formTitle}>Upload Onboarding Document</h1>
        </Grid>
      </Grid>

      <form className={styles.formContainer} onSubmit={handleSubmit}>
        {error && (
          <div style={{ color: '#d32f2f', marginBottom: 16 }} role="alert">
            {error}
          </div>
        )}

        <Alert severity="warning" variant="filled" style={{ marginBottom: 16, fontWeight: 600 }}>
          <strong>Fillable PDFs only.</strong> The document must be an interactive fillable PDF (with
          form fields). Non-fillable or scanned PDFs will not work for employee completion.
        </Alert>

        <div style={{ marginBottom: 16, color: '#666', fontSize: '0.875rem' }}>
          {existingDocumentsCount} of 50 documents uploaded
        </div>

        <div
          role="button"
          tabIndex={0}
          aria-label="Upload PDF file — click or drag and drop"
          style={{
            border: isDragging
              ? '2px dashed #446388'
              : selectedFile
                ? '2px dashed #4caf50'
                : '2px dashed #ccc',
            borderRadius: 8,
            padding: 24,
            textAlign: 'center',
            marginBottom: 16,
            cursor: 'pointer',
            backgroundColor: isDragging ? '#eef2f7' : '#fafafa',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            aria-label="Select PDF file"
          />
          <CloudUpload style={{ fontSize: 48, color: '#999', marginBottom: 8 }} />
          <div style={{ marginBottom: 4 }}>
            {selectedFile ? selectedFile.name : 'Click or drag & drop a PDF file here'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#999' }}>
            Maximum file size: 10MB | Format: Fillable PDF only
          </div>
        </div>

        <TextField
          label="Name"
          name="name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          className={styles.input}
          margin="normal"
          fullWidth
          required
          helperText="Document name shown to employees"
          inputProps={{
            maxLength: 100,
          }}
        />

        <FormControl fullWidth margin="normal" required className={styles.input}>
          <InputLabel>Document Type</InputLabel>
          <Select
            value={selectedTypeId || ''}
            onChange={handleDocumentTypeChange}
            label="Document Type"
            disabled={isUploading}
          >
            {activeTemplateTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div style={{ color: '#666', marginTop: 8, fontSize: '0.875rem' }}>
          Select the type of PDF document
        </div>

        <FormControlLabel
          control={
            <Checkbox
              checked={isManagerSignatureRequired}
              onChange={(e) => setIsManagerSignatureRequired(e.target.checked)}
              disabled={isUploading}
            />
          }
          label="Require Manager Signature"
          style={{ marginTop: 16 }}
        />
        <div style={{ color: '#666', fontSize: '0.875rem', marginLeft: 32 }}>
          Check this if manager signature is required for this document
        </div>
      </form>
    </div>
  );
};

export default UploadDocument;
